'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { formatCurrency } from '@/lib/platform/utils/currency';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/20/solid';

interface SponsorshipValueReportProps {
  totalMediaValue: number;
  totalReach: number;
  brandVisibility: number;
  highlights: {
    label: string;
    value: string;
  }[];
  dateRange?: string;
  previousTotalMediaValue?: number;
  previousTotalReach?: number;
  previousBrandVisibility?: number;
}

export default function SponsorshipValueReport({
  totalMediaValue,
  totalReach,
  brandVisibility,
  highlights,
  dateRange = 'Q4 2025',
  previousTotalMediaValue,
  previousTotalReach,
  previousBrandVisibility,
}: SponsorshipValueReportProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();

  const calculateChange = (current: number, previous?: number) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change).toFixed(1) + '%',
      type: change >= 0 ? 'increase' : 'decrease',
    };
  };

  const stats = [
    {
      name: 'Total Attention Valuation',
      value: formatCurrency(totalMediaValue, baseCurrency),
      change: calculateChange(totalMediaValue, previousTotalMediaValue),
    },
    {
      name: 'Total Reach',
      value: `${(totalReach / 1000000).toFixed(1)}M people`,
      change: calculateChange(totalReach, previousTotalReach),
    },
    {
      name: 'Brand Value Indicator',
      value: `${brandVisibility.toFixed(1)}/10`,
      change: calculateChange(brandVisibility, previousBrandVisibility),
    },
  ];

  return (
    <div className="mb-10">
      <div className="mb-5 flex items-baseline justify-between">
        <h3 className={`text-base font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Sponsorship Value Report
        </h3>
        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {dateRange}
        </span>
      </div>

      {/* Stats Grid */}
      <dl
        className={`grid grid-cols-1 divide-y overflow-hidden rounded-lg shadow-sm md:grid-cols-3 md:divide-x md:divide-y-0 ${
          darkMode ? 'divide-white/10 bg-gray-800 ring-1 ring-white/10' : 'divide-gray-200 bg-white'
        }`}
      >
        {stats.map((item) => (
          <div key={item.name} className="px-4 py-5 sm:p-6">
            <dt className={`text-base font-normal ${darkMode ? 'text-gray-400' : 'text-gray-900'}`}>
              {item.name}
            </dt>
            <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
              <div
                className={`text-2xl font-semibold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
              >
                {item.value}
              </div>

              {item.change && (
                <div
                  className={`inline-flex items-baseline rounded-full px-2.5 py-0.5 text-sm font-medium md:mt-2 lg:mt-0 ${
                    item.change.type === 'increase'
                      ? darkMode
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-green-100 text-green-800'
                      : darkMode
                        ? 'bg-red-500/10 text-red-400'
                        : 'bg-red-100 text-red-800'
                  }`}
                >
                  {item.change.type === 'increase' ? (
                    <ArrowUpIcon
                      aria-hidden="true"
                      className={`mr-0.5 -ml-1 size-5 shrink-0 self-center ${
                        darkMode ? 'text-green-400' : 'text-green-500'
                      }`}
                    />
                  ) : (
                    <ArrowDownIcon
                      aria-hidden="true"
                      className={`mr-0.5 -ml-1 size-5 shrink-0 self-center ${
                        darkMode ? 'text-red-400' : 'text-red-500'
                      }`}
                    />
                  )}
                  <span className="sr-only">
                    {item.change.type === 'increase' ? 'Increased' : 'Decreased'} by{' '}
                  </span>
                  {item.change.percentage}
                </div>
              )}
            </dd>
          </div>
        ))}
      </dl>

      {/* Key Highlights */}
      {highlights.length > 0 && (
        <div
          className={`mt-6 rounded-lg p-6 shadow-sm ring-1 ${
            darkMode ? 'bg-gray-800 ring-white/10' : 'bg-white ring-gray-900/5'
          }`}
        >
          <h4
            className={`mb-4 text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
          >
            Key Highlights
          </h4>
          <ul className="space-y-2">
            {highlights.map((highlight, index) => (
              <li
                key={index}
                className={`flex items-start text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                <span className={`mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>•</span>
                <span>
                  <span className="font-medium">{highlight.label}:</span> {highlight.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
